import { webCoreTypes } from "@core/core-types.di";
import { FallbackError } from "@core/domain/errors/fallback.error";
import type { IotApiClient, ModelError } from "@iot-platform/core";
import type { LatestDataDto } from "@station/data/dto/latest-data.dto";
import type { StationDto } from "@station/data/dto/station.dto.ts";
import { StationMapper } from "@station/data/mappers/station.mapper";
import type { LatestData } from "@station/domain/models/latest-data.model";
import type { Station } from "@station/domain/models/station.model.ts";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class StationDatasource {
    static readonly LATEST_DATA_PATH = "/stations/:stationId/data/latest";

    constructor(
        @inject(webCoreTypes.infrastructure.IotApiClient)
        private readonly iotApiClient: IotApiClient,
        @inject(StationMapper)
        private readonly stationMapper: StationMapper,
    ) {}

    latestData(
        stationId: string,
    ): ResultAsync<
        LatestData[],
        ModelError<typeof LatestData> | FallbackError
    > {
        return this.iotApiClient.http
            .get<LatestDataDto>(
                StationDatasource.LATEST_DATA_PATH.replace(
                    ":stationId",
                    stationId,
                ),
            )
            .mapErr(() => new FallbackError())
            .andThen((response) =>
                this.stationMapper.mapLatestData(response.data),
            );
    }

    getStationBy(
        stationId: number,
    ): ResultAsync<Station, ModelError<typeof Station> | FallbackError> {
        return this.iotApiClient.http
            .get<StationDto>(`/stations/${stationId}`)
            .mapErr(() => new FallbackError())
            .andThen((response) => this.stationMapper.map(response.data));
    }
}
